import React from "react";
import { useCookiePreferences } from "../../hooks/useCookiePreferences";
import useDeviceType from "../../hooks/useDeviceType";
import { BRANDING } from "../../constants/branding";
import { Button } from "../atoms/button";
import { IconButton } from "../atoms/icon-button";
import { Separator } from "../atoms/separator";
import { Eyebrow } from "../atoms/eyebrow";
import { StatusPill } from "../molecules/status-pill";
import { cn } from "../../lib/utils";

interface HeaderProps {
	isRunning?: boolean;
	isDebugging?: boolean;
	isReady?: boolean;
	status?: string;
	statusText?: string;
	onRun?: () => void;
	onDebug?: () => void;
	onStop?: () => void;
	onStepOver?: () => void;
	onStepIn?: () => void;
	onStepOut?: () => void;
	onContinue?: () => void;
	onToggleFullscreen?: () => void;
}

type StatusTone =
	| "ready"
	| "loading"
	| "debugging"
	| "error"
	| "finished"
	| "neutral";

function statusToTone(status: string): StatusTone {
	if (status === "ready") return "ready";
	if (status === "loading") return "loading";
	if (status === "debugging") return "debugging";
	if (status === "error") return "error";
	if (status === "finished") return "finished";
	return "neutral";
}

function statusToLabel(status: string): string {
	if (status === "ready") return BRANDING.status.ready;
	if (status === "loading") return BRANDING.status.loading;
	if (status === "debugging") return BRANDING.status.learning;
	if (status === "error") return BRANDING.status.error;
	if (status === "finished") return BRANDING.status.finished;
	return "Active";
}

const Header: React.FC<HeaderProps> = ({
	isDebugging = false,
	isReady = true,
	status = "ready",
	statusText = "Ready",
	onRun = () => {},
	onDebug = () => {},
	onStop = () => {},
	onToggleFullscreen = () => {},
}) => {
	const { isDarkMode, toggleDarkMode } = useCookiePreferences();
	const { isMobile, isTablet } = useDeviceType();

	const tone = statusToTone(status);
	const label = statusToLabel(status);

	return (
		<header
			className={cn(
				"sticky top-0 z-50 glass",
				"bg-white/85 dark:bg-[#0a0a0b]/85",
				"border-b border-gray-200/80 dark:border-white/[0.06]",
				"px-4 lg:px-6 h-[60px] flex items-center",
				"transition-colors duration-200 animate-fade-in",
			)}
		>
			<div className="flex justify-between items-center w-full">
				{/* Brand + Status */}
				<div className="flex items-center gap-4 min-w-0 flex-1">
					<div className="flex items-center gap-2.5 min-w-0">
						<div className="relative group flex-shrink-0">
							<img
								src="/images/logo.png"
								alt={`${BRANDING.appName} Logo`}
								className="h-7 w-auto transition-transform duration-200 group-hover:rotate-[-4deg]"
							/>
						</div>
						{!isMobile && (
							<div className="flex items-baseline gap-2.5 min-w-0">
								<h1
									className="font-display text-[22px] leading-none tracking-[-0.035em] [font-feature-settings:'ss01']"
									style={{ fontVariationSettings: '"opsz" 96, "wght" 600' }}
								>
									<span className="text-gray-900 dark:text-gray-50">
										{BRANDING.appNameParts.prefix}
									</span>
									<span className="text-orange-600 dark:text-amber-400">
										{BRANDING.appNameParts.suffix}
									</span>
								</h1>
								{!isTablet && (
									<>
										<span className="text-gray-300 dark:text-gray-700 text-sm select-none">
											/
										</span>
										<Eyebrow className="!text-[10px] !tracking-[0.14em] whitespace-nowrap">
											Python Studio
										</Eyebrow>
									</>
								)}
							</div>
						)}
					</div>

					<StatusPill
						tone={tone}
						label={!isMobile ? label : undefined}
						pulse={status === "loading" || status === "debugging"}
						title={statusText}
						className="cursor-help"
					/>
				</div>

				{/* Controls */}
				<div className="flex items-center gap-1.5">
					<IconButton
						onClick={toggleDarkMode}
						title={`Switch to ${isDarkMode ? "light" : "dark"} mode`}
					>
						{isDarkMode ? (
							<svg
								className="w-3.5 h-3.5"
								fill="none"
								stroke="currentColor"
								strokeWidth="1.7"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
								/>
							</svg>
						) : (
							<svg
								className="w-3.5 h-3.5"
								fill="none"
								stroke="currentColor"
								strokeWidth="1.7"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
								/>
							</svg>
						)}
					</IconButton>

					<IconButton
						onClick={onToggleFullscreen}
						title="Toggle fullscreen (F11 or F)"
					>
						<svg
							className="w-3.5 h-3.5"
							fill="none"
							stroke="currentColor"
							strokeWidth="1.7"
							viewBox="0 0 24 24"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								d="M4 8V4m0 0h4m-4 0l5.657 5.657M20 8V4m0 0h-4m4 0l-5.657 5.657M4 16v4m0 0h4m-4 0l5.657-5.657M20 16v4m0 0h-4m4 0l-5.657-5.657"
							/>
						</svg>
					</IconButton>

					<Separator orientation="vertical" className="h-5 mx-1" />

					<Button
						variant="success"
						size="sm"
						onClick={onRun}
						disabled={!isReady}
						title="Run code (⌘/Ctrl+Enter)"
					>
						<svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
							<path d="M8 5v14l11-7z" />
						</svg>
						{!isMobile && (
							<span>{isTablet ? "Run" : BRANDING.buttons.runCode}</span>
						)}
					</Button>

					{!isMobile && (
						<Button
							variant="warningOutline"
							size="sm"
							onClick={onDebug}
							disabled={!isReady || isDebugging}
							title="Step through code (⌘/Ctrl+D)"
						>
							<svg
								className="w-3 h-3"
								fill="none"
								stroke="currentColor"
								strokeWidth="2"
								viewBox="0 0 24 24"
							>
								<path
									strokeLinecap="round"
									strokeLinejoin="round"
									d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
								/>
							</svg>
							<span>{isTablet ? "Learn" : BRANDING.buttons.learnMode}</span>
						</Button>
					)}

					{isDebugging && (
						<Button
							variant="danger"
							size="sm"
							onClick={onStop}
							title="Stop (Esc)"
							className="animate-fade-in"
						>
							<svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
								<rect x="6" y="6" width="12" height="12" rx="1.5" />
							</svg>
							{!isMobile && <span>Stop</span>}
						</Button>
					)}
				</div>
			</div>
		</header>
	);
};

export default Header;
